import React, { useContext, useEffect, useMemo, useState } from "react";
import { Card, CardBody, Container } from "reactstrap";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { BreadcrumbContext } from "../../../../Layouts/Header";
import { VOLUNTEER_PATHS } from "Routes/paths";
import DeleteModal from "Components/Common/DeleteModal";
import commonStyles from "../../../../assets/scss/common/common.module.scss";
import styles from "../css/volunteerstudyhistory.module.scss";
import {
  GetUserData,
  perPageData,
  showToastMessage,
} from "common/service/CommonService";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import { StudyForVolunteer } from "../helper/volunteerstudyhistory.types";
import { useVolunteerStudyHistoryDetailsService } from "../api/useVolunteerStudyHistoryDetailsService";
import { studyColumn } from "../helper/volunteerstudyhistory.constant";

/* ================= PROPS ================= */

interface Props {
  volunteerId: number;
  status: "Ongoing" | "Completed";
}

/* ================= COMPONENT ================= */

const STUDY_TABS = [
  { id: "completed", label: "Completed", icon: "ri-checkbox-circle-line" },
  { id: "passed", label: "Screening Passed", icon: "ri-shield-check-line" },
  { id: "failed", label: "Screening Failed", icon: "ri-close-circle-line" },
];

const VolunteerStudyHistoryList: React.FC<{ volunteerid?: number }> = ({ volunteerid }) => {
  const params = useParams();
  const activeVolunteerId = volunteerid ?? Number(params?.id);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [activeTab, setActiveTab] = useState<string>("completed");

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] =
    useState<StudyForVolunteer | null>(null);

  const breadcrumbContext = useContext(BreadcrumbContext);
  const setBreadcrumbItems = breadcrumbContext?.setBreadcrumbItems;

  useEffect(() => {
    setBreadcrumbItems?.([
      { text: "Volunteers", link: VOLUNTEER_PATHS.LIST },
      { text: "Study History" },
    ]);

    return () => {
      setBreadcrumbItems?.([]);
    };
  }, []);

  /* -----------------------------
 Fetch Studies For Volunteer
----------------------------- */

  const { VolunteerStudyDetailsQuery, DeleteMutation } = useVolunteerStudyHistoryDetailsService({
    volunteerid: activeVolunteerId,
    status: activeTab,
    searchText,
    pageNumber: currentPageNumber,
    pageSize: perPageData,
  });

  const {
    data: queryResponse,
    isLoading,
    isFetching,
    isError,
  } = VolunteerStudyDetailsQuery as {
    data?: {
      data?: {
        studies?: any[];
      };
    };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const data: (StudyForVolunteer & { total_count: number })[] = useMemo(() => {
    const resData = queryResponse?.data;
    if (!resData) return [];
    return resData.studies ?? [];
  }, [queryResponse]);

  /* -----------------------------
     Delete Flow
  ----------------------------- */
  const ToggleDeleteResason = (): void => {
    setShowDeleteReason((prev) => !prev);
  };

  const onDeleteClick = (item: StudyForVolunteer) => {
    setSelectedRecord(item);
    setDeleteModal(true);
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }) => {
    if (!selectedRecord) return;

    const payload = {
      volunteerid: activeVolunteerId,
      studyid: reasonPayload.id,
      updatereason: reasonPayload.reason,
    };

    DeleteMutation.mutate(payload, {
      onSuccess: (response: any) => {
        refreshTableData();
        showToastMessage("success", response?.message);
        setDeleteModal(false);
        setSelectedRecord(null);
        ToggleDeleteResason();
      },
      onError: (error: any) => {
        showToastMessage("error", error?.message || "Failed to remove study");
      },
    });
  };

  //-------------------------------------------------------------------------------------------
  //--> On Reset Table Record
  //-------------------------------------------------------------------------------------------

  const refreshTableData = () => {
    if (currentPageNumber === 1) {
      queryClient.invalidateQueries({
        queryKey: ["volunteer-assignedstudy-selectAll"],
      });
    } else {
      setCurrentPageNumber(1);
    }
  };

  //-------------------------------------------------------------------------------------------
  //--> ON SEARCH
  //-------------------------------------------------------------------------------------------

  const onSearch = (text: string | null) => {
    setSearchText(text);
    refreshTableData();
  };

  /* ================= RENDER ================= */

  return (
    <Container fluid className="px-0">
      <Card className={commonStyles.card}>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
            {/* Left side: 3 Tabs (Completed, Screening Passed, Screening Failed) */}
            <div className={styles.tabs_container}>
              {STUDY_TABS.map((tab) => (
                <button
                  key={tab.id}
                  className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${styles.tab_btn} ${activeTab === tab.id ? styles.active : ""
                    }`}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setCurrentPageNumber(1);
                  }}
                >
                  <i className={`${tab.icon} me-1`} /> {tab.label}
                </button>
              ))}
            </div>

            {/* Right side: Search bar */}
            <div>
              <CommonSearch
                onSearch={onSearch}
                searchText={searchText}
                placeholder="Search..."
              />
            </div>
          </div>
          <CommonTableIndexWise
            columns={studyColumn(navigate, activeVolunteerId)}
            data={data}
            hideAction={true}
            loading={isLoading || isFetching}
            pageSize={perPageData}
            currentPage={currentPageNumber}
            onPageChange={setCurrentPageNumber}
            totalRecords={data[0]?.total_count || 0}
          />
        </CardBody>
      </Card>

      {/* Delete Modal With Reason */}
      {deleteModal && (
        <DeleteModal
          isopen={deleteModal}
          toggle={() => setDeleteModal(false)}
          recordId={selectedRecord?.studyid ?? 0}
          recordName={selectedRecord?.studyTitle}
          onDeleteClick={(payload: any) => confirmDelete(payload)}
          isLoading={DeleteMutation.isPending}
          showDeleteReason={showDeleteReason}
          toggleDeleteReason={ToggleDeleteResason}
        />
      )}
    </Container>
  );
};

export default VolunteerStudyHistoryList;
